import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

// Page Header Component
export const PageHeader = ({ title, subtitle, breadcrumb }) => (
  <section style={{
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    padding: '140px 24px 80px',
    position: 'relative',
    overflow: 'hidden'
  }}>
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      {[...Array(8)].map((_, i) => (
        <div key={i} style={{
          position: 'absolute',
          width: Math.random() * 300 + 100,
          height: Math.random() * 300 + 100,
          background: `radial-gradient(circle, rgba(255,107,53,${Math.random() * 0.1}) 0%, transparent 70%)`,
          borderRadius: '50%',
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
        }} />
      ))}
    </div>
    <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 10 }}>
      {breadcrumb && (
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Link to="/" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>Home</Link>
          <span style={{ color: 'rgba(255,255,255,0.4)' }}>/</span>
          <span style={{ color: '#ff6b35', fontSize: 14 }}>{breadcrumb}</span>
        </div>
      )}
      <h1 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 800, color: 'white', marginBottom: 16 }}>{title}</h1>
      {subtitle && <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.8)', maxWidth: 600, lineHeight: 1.7 }}>{subtitle}</p>}
    </div>
  </section>
);

// CTA Section Component
export const CTASection = ({ title, subtitle, buttonText, dark = true }) => (
  <section style={{
    padding: '80px 24px',
    background: dark ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' : '#f8f9fa',
    textAlign: 'center'
  }}>
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: dark ? 'white' : '#1a1a2e', marginBottom: 16 }}>
        {title || "Ready to Grow Your Business?"}
      </h2>
      <p style={{ fontSize: 17, color: dark ? 'rgba(255,255,255,0.8)' : '#666', lineHeight: 1.7, marginBottom: 32 }}>
        {subtitle || "Schedule a free strategy call and discover how we can help you dominate your local market."}
      </p>
      <Link to="/contact" className="btn-primary" style={{ padding: '16px 40px', borderRadius: 50, fontSize: 16, display: 'inline-block' }}>
        {buttonText || "Get Your Free Strategy Call →"}
      </Link>
    </div>
  </section>
);

// Intersection Observer Hook
export const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        if (options.once !== false) observer.unobserve(entry.target);
      }
    }, { threshold: options.threshold || 0.1, ...options });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, isInView];
};

// Scroll Reveal Component
export const ScrollReveal = ({ children, delay = 0 }) => {
  const [ref, isInView] = useInView();
  return (
    <div ref={ref} style={{
      opacity: isInView ? 1 : 0,
      transform: isInView ? 'translateY(0)' : 'translateY(30px)',
      transition: `all 0.6s ease ${delay}s`
    }}>
      {children}
    </div>
  );
};

// Animated Counter
export const AnimatedCounter = ({ end, duration = 2000, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const [ref, isInView] = useInView();
  useEffect(() => {
    if (!isInView) return;
    let startTime = null;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isInView, end, duration]);
  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

// Stats Bar Component
export const StatsBar = () => (
  <section style={{ background: 'white', padding: '40px 24px', borderBottom: '1px solid #eee' }}>
    <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'center', gap: 60, flexWrap: 'wrap' }}>
      {[
        { value: <AnimatedCounter end={500} suffix="K+" prefix="$" />, label: 'Ad Spend Managed' },
        { value: <AnimatedCounter end={50} suffix="+" />, label: 'Happy Clients' },
        { value: <AnimatedCounter end={312} suffix="%" />, label: 'Avg Traffic Growth' },
        { value: <AnimatedCounter end={40} suffix="%" />, label: 'Lower Cost Per Lead' },
      ].map((stat, i) => (
        <div key={i} style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 36, fontWeight: 800, color: '#ff6b35' }}>{stat.value}</div>
          <div style={{ color: '#666', fontSize: 14, marginTop: 4 }}>{stat.label}</div>
        </div>
      ))}
    </div>
  </section>
);

// Service Card Component
export const ServiceCard = ({ icon, title, description, features, link }) => (
  <Link to={link} className="card" style={{
    background: 'white', borderRadius: 20, padding: 32,
    border: '1px solid #eee', transition: 'all 0.3s ease',
    display: 'block', height: '100%'
  }}>
    <div style={{ fontSize: 48, marginBottom: 16 }}>{icon}</div>
    <h3 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>{title}</h3>
    <p style={{ color: '#666', lineHeight: 1.7, marginBottom: 20, fontSize: 15 }}>{description}</p>
    {features && (
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {features.slice(0, 4).map((f, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, color: '#555', fontSize: 14 }}>
            <span style={{ color: '#ff6b35' }}>✓</span> {f}
          </li>
        ))}
      </ul>
    )}
    <div style={{ marginTop: 20, color: '#ff6b35', fontWeight: 600, fontSize: 14 }}>
      Learn More →
    </div>
  </Link>
);

// Testimonial Card
export const TestimonialCard = ({ name, company, text }) => (
  <div style={{ background: '#f8f9fa', borderRadius: 16, padding: 28 }}>
    <div style={{ color: '#ff6b35', fontSize: 20, marginBottom: 12 }}>★★★★★</div>
    <p style={{ color: '#444', fontSize: 15, lineHeight: 1.7, marginBottom: 16, fontStyle: 'italic' }}>"{text}"</p>
    <div>
      <div style={{ fontWeight: 700, color: '#1a1a2e', fontSize: 15 }}>{name}</div>
      <div style={{ color: '#888', fontSize: 13 }}>{company}</div>
    </div>
  </div>
);

// Trust Badges
export const TrustBadges = () => (
  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
    {[
      { icon: '🏆', label: 'Top Rated Plus' },
      { icon: '📈', label: '50+ Clients' },
      { icon: '💰', label: '$500K+ Managed' },
      { icon: '⭐', label: '4.9/5 Rating' }
    ].map((badge, i) => (
      <div key={i} style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: 'rgba(255,107,53,0.1)', padding: '10px 18px', borderRadius: 50
      }}>
        <span style={{ fontSize: 18 }}>{badge.icon}</span>
        <span style={{ color: '#1a1a2e', fontSize: 14, fontWeight: 600 }}>{badge.label}</span>
      </div>
    ))}
  </div>
);
